import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Chip,
  TextField,
  Grid,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  Tooltip
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../contexts/ApiContext';
import { useAuth } from '../contexts/AuthContext';

// Generate closing period options
const generateClosingPeriodOptions = () => {
  const options = [];
  for (let hour = 0; hour < 24; hour++) {
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    const period = hour < 12 ? 'am' : 'pm';
    const nextHour = (hour + 1) % 24;
    const nextDisplayHour = nextHour === 0 ? 12 : nextHour > 12 ? nextHour - 12 : nextHour;
    const nextPeriod = nextHour < 12 ? 'am' : 'pm';
    
    // Display format: "12:00am - 12:00am" but store the hour value
    const label = hour === 0 
      ? '12:00am - 12:00am' 
      : `${displayHour}:00${period} - ${nextDisplayHour}:00${nextPeriod}`;
    
    options.push({ value: hour, label });
  }
  return options;
};

const closingPeriodOptions = generateClosingPeriodOptions();

// Memoized table row component to prevent unnecessary re-renders
const LocationRow = React.memo(({ location, onViewDetails, onClosingPeriodChange, isAdmin, user }) => {
  
  return (
    <TableRow hover>
      <TableCell>{location.LocationName}</TableCell>
      <TableCell>
        <Box display="flex" flexWrap="wrap" gap={0.5}>
          {location.TerminalIDs && location.TerminalIDs.slice(0, 3).map((terminalId) => (
            <Chip
              key={terminalId}
              label={terminalId}
              size="small"
              variant="outlined"
            />
          ))}
          {location.TerminalIDs && location.TerminalIDs.length > 3 && (
            <Chip
              label={`+${location.TerminalIDs.length - 3} more`}
              size="small"
              variant="outlined"
              color="primary"
            />
          )}
        </Box>
      </TableCell>
      <TableCell>{location.FirstSettlement}</TableCell>
      <TableCell>{location.LastSettlement}</TableCell>
      <TableCell>
        <FormControl size="small" fullWidth disabled={!isAdmin}>
          <Select
            value={location.closingHour || 0}
            onChange={(e) => onClosingPeriodChange(location.LocationName, e.target.value)}
            displayEmpty
          >
            {closingPeriodOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </TableCell>
      <TableCell align="center">
        <Button
          variant="outlined"
          size="small"
          onClick={() => onViewDetails(location.LocationName)}
        >
          View Details
        </Button>
      </TableCell>
    </TableRow>
  );
});

const Locations = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const searchTimeoutRef = useRef(null);
  const navigate = useNavigate();
  const { getLocations, updateLocationClosingPeriod } = useApi();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    loadLocations();
  }, []);

  // Optimized debounce with ref to prevent state updates
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    if (searchTerm !== debouncedSearchTerm) {
      setIsSearching(true);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setIsSearching(false);
    }, 100); // Reduced to 100ms for responsive feel

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm, debouncedSearchTerm]);

  const loadLocations = async () => {
    try {
      setLoading(true);
      const response = await getLocations();
      setLocations(response || []);
    } catch (err) {
      setError(err.message || 'Failed to load locations');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = useCallback((locationName) => {
    navigate(`/locations/${encodeURIComponent(locationName)}`);
  }, [navigate]);

  const handleClosingPeriodChange = useCallback(async (locationName, closingHour) => {
    if (!isAdmin) return;
    
    try {
      // Update the local state immediately for responsiveness
      setLocations(prevLocations => 
        prevLocations.map(loc => 
          loc.LocationName === locationName 
            ? { ...loc, closingHour: parseInt(closingHour) }
            : loc
        )
      );
      
      // Call the API to update the backend
      await updateLocationClosingPeriod(locationName, closingHour);
    } catch (err) {
      console.error('Failed to update closing period:', err);
      setError('Failed to update closing period. Please try again.');
      // Reload locations to get the correct state
      loadLocations();
    }
  }, [isAdmin, updateLocationClosingPeriod]);

  const formatCurrency = useCallback((amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  }, []);

  const formatNumber = useCallback((num) => {
    return new Intl.NumberFormat('en-US').format(num);
  }, []);

  // Optimize filtering with early returns and caching
  const filteredLocations = useMemo(() => {
    if (!debouncedSearchTerm.trim()) {
      return locations;
    }
    
    const searchLower = debouncedSearchTerm.toLowerCase();
    const searchTerms = searchLower.split(' ').filter(term => term.length > 0);
    
    return locations.filter(location => {
      // Cache lowercase values
      const locationNameLower = location.LocationName?.toLowerCase() || '';
      const terminalIdsLower = location.TerminalIDs?.join(' ').toLowerCase() || '';
      
      // Check if all search terms match either location name or terminal IDs
      return searchTerms.every(term => 
        locationNameLower.includes(term) ||
        terminalIdsLower.includes(term)
      );
    });
  }, [locations, debouncedSearchTerm]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Locations
      </Typography>
      
      {/* Search Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Search Locations"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
                placeholder="Search by Location Name or Terminal ID"
                InputProps={{
                  endAdornment: isSearching && <CircularProgress size={20} />
                }}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <Typography variant="body2" color="text.secondary">
                Showing {filteredLocations.length} of {locations.length} locations
                {debouncedSearchTerm && ` matching "${debouncedSearchTerm}"`}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Location Summary
          </Typography>
          
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Location Name</strong></TableCell>
                  <TableCell><strong>Terminal IDs</strong></TableCell>
                  <TableCell><strong>First Settlement</strong></TableCell>
                  <TableCell><strong>Last Settlement</strong></TableCell>
                  <TableCell><strong>Closing Period</strong></TableCell>
                  <TableCell align="center"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredLocations.map((location) => (
                  <LocationRow
                    key={location.LocationName}
                    location={location}
                    onViewDetails={handleViewDetails}
                    onClosingPeriodChange={handleClosingPeriodChange}
                    isAdmin={isAdmin}
                    user={user}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          {locations.length === 0 && (
            <Box textAlign="center" p={3}>
              <Typography variant="body1" color="textSecondary">
                No locations found. Please check your permissions or ensure data has been uploaded.
              </Typography>
            </Box>
          )}
          
          {locations.length > 0 && filteredLocations.length === 0 && debouncedSearchTerm && (
            <Box textAlign="center" p={3}>
              <Typography variant="body1" color="textSecondary">
                No locations match your search criteria "{debouncedSearchTerm}".
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Try adjusting your search terms or clearing the search to see all locations.
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Locations;